"use client";

import { useEffect, useMemo, useState } from "react";
import type { ProvidersApiResponse } from "@/app/api/providers/route";

type ProviderCard = ProvidersApiResponse["providers"][number];

function keyProviders(providers: ProviderCard[]) {
  return providers.map((provider) => provider.slug).join("|");
}

export function useResolvedCompareProviders(
  slugs: string[],
  localProviders: ProviderCard[],
) {
  const [resolvedProviders, setResolvedProviders] = useState<ProviderCard[]>([]);
  const localKey = keyProviders(localProviders);

  useEffect(() => {
    if (slugs.length === 0) {
      setResolvedProviders([]);
      return;
    }

    const localProvidersBySlug = new Map(
      localProviders.map((provider) => [provider.slug, provider]),
    );
    const missingSlugs = slugs.filter((slug) => !localProvidersBySlug.has(slug));

    if (missingSlugs.length === 0) {
      setResolvedProviders(
        slugs.flatMap((slug) => {
          const provider = localProvidersBySlug.get(slug);
          return provider ? [provider] : [];
        }),
      );
      return;
    }

    const controller = new AbortController();

    async function fetchMissingProviders() {
      try {
        const params = new URLSearchParams({
          slugs: missingSlugs.join(","),
        });
        const res = await fetch(`/api/providers?${params.toString()}`, {
          signal: controller.signal,
        });

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const data = (await res.json()) as ProvidersApiResponse;
        const fetchedProvidersBySlug = new Map(
          data.providers.map((provider) => [provider.slug, provider]),
        );

        setResolvedProviders(
          slugs.flatMap((slug) => {
            const provider =
              localProvidersBySlug.get(slug) ?? fetchedProvidersBySlug.get(slug);
            return provider ? [provider] : [];
          }),
        );
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }

        setResolvedProviders(
          slugs.flatMap((slug) => {
            const provider = localProvidersBySlug.get(slug);
            return provider ? [provider] : [];
          }),
        );
      }
    }

    void fetchMissingProviders();

    return () => controller.abort();
  }, [slugs, localKey, localProviders]);

  return useMemo(
    () =>
      slugs.flatMap((slug) => {
        const provider = resolvedProviders.find((item) => item.slug === slug);
        return provider ? [provider] : [];
      }),
    [resolvedProviders, slugs],
  );
}
